"use client";

import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { logCodeAction } from "@/lib/codeAuditLog";

interface DeleteUserDialogProps {
  user: { id: string; email: string };
  onUpdate: () => Promise<void> | void; // ex.: fetchUsers
}

export function DeleteUserDialog({ user, onUpdate }: DeleteUserDialogProps) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"idle" | "teams" | "auth">("idle");
  const { toast } = useToast();
  const { user: actor } = useAuth();

  async function handleDelete() {
    setLoading(true);
    try {
      // 1) apaga vínculos em user_teams (e outras filhas, se existir)
      setStep("teams");
      const { error: childrenError } = await supabase.rpc(
        "hard_delete_user_children",
        { p_user: user.id }
      );
      if (childrenError) {
        throw new Error(
          childrenError.message || "Falha ao remover vínculos (user_teams)"
        );
      }

      // 2) apaga no Auth (GoTrue) via service role no servidor
      setStep("auth");
      const serviceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
      if (!serviceRoleKey) {
        throw new Error(
          "Service role key ausente (VITE_SUPABASE_SERVICE_ROLE_KEY)."
        );
      }

      const adminUrl = `${
        import.meta.env.VITE_SUPABASE_URL
      }/auth/v1/admin/users/${user.id}?hard=true`;
      const response = await fetch(adminUrl, {
        method: "DELETE",
        headers: {
          apikey: serviceRoleKey,
          Authorization: `Bearer ${serviceRoleKey}`,
        },
      });

      if (!response.ok) {
        let message = `Falha ao remover no Auth: ${response.status}`;
        try {
          const body = await response.json();
          message = body?.message || message;
        } catch (_) {
          // ignore body parse errors
        }
        throw new Error(message);
      }

      if (actor) {
        await logCodeAction({
          userId: actor.id,
          userEmail: actor.email ?? "",
          action: "member_deleted",
          codeType: "member",
          referenceId: user.id,
          codeLabel: user.email,
        });
      }

      // refresh lista
      await Promise.resolve(onUpdate());

      toast({
        title: "Usuário removido",
        description: `O usuário ${user.email} foi removido com sucesso.`,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Erro ao remover usuário",
        description: err?.message ?? "Erro ao apagar usuário.",
      });
    } finally {
      setLoading(false);
      setStep("idle");
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          className="p-2 rounded-full bg-secondary text-destructive hover:bg-accent hover:text-destructive disabled:opacity-50"
          disabled={loading}
          aria-label="Remover usuário"
          title="Remover usuário"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </AlertDialogTrigger>

      <AlertDialogContent className="bg-card border border-border text-foreground">
        <AlertDialogHeader>
          <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            Você está prestes a remover o usuário{" "}
            <b className="text-foreground">{user.email}</b>.
            <br />
            Esta ação é <span className="text-destructive">irreversível</span>.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="text-sm text-muted-foreground">
          {loading ? (
            <>
              {step === "teams" && "Removendo vínculos (user_teams)..."}
              {step === "auth" && "Removendo usuário no Auth..."}
            </>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={loading}
            className="bg-secondary text-foreground hover:bg-accent"
          >
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Removendo..." : "Remover usuário"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
